import React, { useEffect, useState } from 'react';
import {
  characterLimit,
  emptyValueError,
  duplicatesError,
  filterPresetSuccess,
  PaperPropsWithoutScrollbar,
} from 'constants';
import {
  onSave,
  refinePayload,
  useNotification,
  getDialogHeading,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import {
  mapToIdValue,
  handleIdsChange,
  handleAddIdOnEnter,
  useStudentSearchEffect,
} from './FilterPresetUtils';
import {
  putFilterPreset,
  postFilterPreset,
} from '../services/filterPresetsApi';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { GridForm, MultipleIDsGrid } from 'components/Ui/styled';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { FormInputSwitch } from 'components/Common/Forms/FormInputSwitch';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { FormInputTextIcon } from 'components/Common/Forms/FormTextFieldIcon';
import { attendanceTagsDropdown } from 'layouts/applications/attendance/common/constants';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { getStudentProfileFilterOptions } from 'layouts/studentManagement/services/studentProfileApi';
import { setStudentProfileFilterOptions } from 'layouts/studentManagement/store/studentProfileFilterOptionsSlice/studentProfileFilterOptionsSlice';

const filterPresetsSchema = yup.object().shape({
  presetName: yup
    .string()
    .required('Preset name is required')
    .max(
      characterLimit,
      `Preset name cannot exceed ${characterLimit} characters`
    ),
  // .matches(/^\S*$/, 'Preset name cannot contain spaces'),
});

const FilterPresetsDialog = (props) => {
  const {
    open,
    setOpen,
    isNewFilterPreset,
    filterPresetsForm,
    setIsNewFilterPreset,
    setFilterPresetsForm,
    fetchFilterPresetsListData,
  } = props;

  const dispatch = useDispatch();
  const {
    NotificationPopup,
    setOpenNotification,
    handleErrorResponse,
    setNotificationMessage,
  } = useNotification();

  const {
    presetName,
    courses,
    includeIdsExclusive,
    instructors,
    tags,
    types,
    grades,
    excludeIds: excludeIdsData,
    includeIds: includeIdsData,
  } = filterPresetsForm || {};

  const [excludeIds, setExcludeIds] = useState([]);
  const [includeIds, setIncludeIds] = useState([]);
  const [excludeSelect, setExcludeSelect] = useState(null);
  const [includeSelect, setIncludeSelect] = useState(null);
  const [excludeOptions, setExcludeOptions] = useState([]);
  const [includeOptions, setIncludeOptions] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [isExcludeLoading, setIsExcludeLoading] = useState(false);
  const [isIncludeLoading, setIsIncludeLoading] = useState(false);
  const [excludeSearchQuery, setExcludeSearchQuery] = useState('');
  const [includeSearchQuery, setIncludeSearchQuery] = useState('');

  const defaultValue = {
    courses: courses,
    excludeIds: excludeIdsData ? excludeIdsData.split(',') : [],
    grades: grades,
    includeIds: includeIdsData ? includeIdsData.split(',') : [],
    includeIdsExclusive: includeIdsExclusive,
    instructors: instructors,
    presetName: presetName,
    tags: tags,
    types: types,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(filterPresetsSchema),
  });

  useEffect(() => {
    if (!open) return;
    if (isNewFilterPreset && !filterPresetsForm) {
      reset({
        presetName: '',
        courses: [],
        grades: [],
        types: [],
        tags: [],
        instructors: [],
        includeIdsExclusive: false,
        excludeIds: [],
        includeIds: [],
      });
      setExcludeIds([]);
      setIncludeIds([]);
    } else if (filterPresetsForm) {
      reset(defaultValue);
      setExcludeIds(defaultValue.excludeIds || []);
      setIncludeIds(defaultValue.includeIds || []);
    }
  }, [open]);

  const { handleSubmit, reset, control, formState, register, watch, setValue } =
    useFunction;
  const { errors } = formState;

  const studentProfileFilterOptions = useSelector(
    (state) => state.studentProfileFilterOptions
  );

  const { allStudentTypes, allInstructors, allCourseNames, allGradeLevels } =
    studentProfileFilterOptions ?? [];

  const allStudentTypesOptions = mapToIdValue(allStudentTypes);
  const allInstructorsOptions = mapToIdValue(allInstructors);
  const allCourseNamesOptions = mapToIdValue(allCourseNames);
  const allGradeLevelsOptions = mapToIdValue(allGradeLevels);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
    setExcludeIds([]);
    setIncludeIds([]);
    setExcludeOptions([]);
    setIncludeOptions([]);
    setExcludeSelect(null);
    setIncludeSelect(null);
    setExcludeSearchQuery('');
    setIncludeSearchQuery('');
    setFilterPresetsForm(null);
    setIsNewFilterPreset(false);
  };

  const getStudentProfileFilterOptionsData = async () => {
    try {
      const studentProfileFilterOptionsData =
        await getStudentProfileFilterOptions();
      dispatch(setStudentProfileFilterOptions(studentProfileFilterOptionsData));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    getStudentProfileFilterOptionsData();
  }, []);

  useStudentSearchEffect(
    excludeSearchQuery,
    setIsExcludeLoading,
    setExcludeOptions,
    handleErrorResponse
  );
  useStudentSearchEffect(
    includeSearchQuery,
    setIsIncludeLoading,
    setIncludeOptions,
    handleErrorResponse
  );

  const handleExcludeId = (e) => {
    if (
      handleAddIdOnEnter({
        event: e,
        selected: excludeSelect,
        ids: excludeIds,
        setIds: setExcludeIds,
        setValue,
        field: 'excludeIds',
        notify: (msg) => {
          setOpenNotification(true);
          setNotificationMessage(msg);
        },
        emptyMsg: emptyValueError,
        duplicateMsg: duplicatesError,
      })
    ) {
      setExcludeSelect(null);
    }
  };

  // Include IDs
  const handleIncludeId = (e) => {
    if (
      handleAddIdOnEnter({
        event: e,
        selected: includeSelect,
        ids: includeIds,
        setIds: setIncludeIds,
        setValue,
        field: 'includeIds',
        notify: (msg) => {
          setOpenNotification(true);
          setNotificationMessage(msg);
        },
        emptyMsg: emptyValueError,
        duplicateMsg: duplicatesError,
      })
    ) {
      setIncludeSelect(null);
    }
  };

  const handleExcludeIdsChange = (value) => {
    handleIdsChange(value, setExcludeIds, setValue, 'excludeIds');
  };

  const handleIncludeIdsChange = (value) => {
    handleIdsChange(value, setIncludeIds, setValue, 'includeIds');
  };

  const selectedTags = watch('tags') || [];
  const selectedGrades = watch('grades') || [];
  const selectedCourses = watch('courses') || [];
  const selectedStudentTypes = watch('types') || [];
  const selectedInstructors = watch('instructors') || [];

  const onSubmit = (data) => {
    const payload = {
      ...data,
      excludeIds: excludeIds.join(','),
      includeIds: includeIds.join(','),
    };
    onSave(
      filterPresetSuccess,
      isNewFilterPreset,
      postFilterPreset,
      putFilterPreset,
      refinePayload(payload),
      presetName,
      fetchFilterPresetsListData,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton
    );
    // setIsNewFilterPreset(false);
  };

  const getOptionLabel = (option) => `${option.studentId}`;

  return (
    <>
      <Dialog open={open} PaperProps={PaperPropsWithoutScrollbar}>
        {getDialogHeading('Filter Preset', handleClose)}

        <GridForm container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInputText
              type="text"
              name="presetName"
              label="Preset Name"
              control={control}
              required={true}
              errors={!!errors.presetName}
              readOnly={!isNewFilterPreset}
              helperText={errors?.presetName?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInputDropdown
              multiple
              name="types"
              label="Student Types"
              control={control}
              checked={selectedStudentTypes}
              options={allStudentTypesOptions}
              defaultValue={defaultValue.types}
              renderValue={(selected) => selected.join(', ')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputDropdown
              multiple
              name="instructors"
              label="Instructors"
              control={control}
              checked={selectedInstructors}
              options={allInstructorsOptions}
              defaultValue={defaultValue.instructors}
              renderValue={(selected) => selected.join(', ')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputDropdown
              multiple
              name="courses"
              label="Courses"
              control={control}
              checked={selectedCourses}
              options={allCourseNamesOptions}
              defaultValue={defaultValue.courses}
              renderValue={(selected) => selected.join(', ')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputDropdown
              multiple
              name="grades"
              label="Grades"
              control={control}
              checked={selectedGrades}
              options={allGradeLevelsOptions}
              defaultValue={defaultValue.grades}
              renderValue={(selected) => selected.join(', ')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputDropdown
              multiple
              name="tags"
              label="Tags"
              control={control}
              checked={selectedTags}
              options={attendanceTagsDropdown}
              defaultValue={defaultValue.tags}
              renderValue={(selected) => selected.join(', ')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputSwitch
              control={control}
              name="includeIdsExclusive"
              label="Include Ids Exclusive"
              checked={defaultValue.includeIdsExclusive}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInputAutoCompleteSearch
              label="Exclude Ids"
              options={excludeOptions}
              onKeyDown={handleExcludeId}
              getOptionLabel={getOptionLabel}
              isSearchloading={isExcludeLoading}
              setSelectedInput={setExcludeSelect}
              setInputSearchQuery={setExcludeSearchQuery}
              placeholder="Search students to exclude and press enter"
            />
            <Typography variant="h6">Excluded IDs</Typography>
            <MultipleIDsGrid container>
              <Grid item xs={12}>
                <FormInputTextIcon
                  name="excludeIds"
                  type="text"
                  control={control}
                  label=""
                  register={register('excludeIds', {
                    onChange: (e) => handleExcludeIdsChange(e.target.value),
                  })}
                />
              </Grid>
            </MultipleIDsGrid>
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormInputAutoCompleteSearch
              label="Include Ids"
              placeholder="Search students to include and press enter"
              onKeyDown={handleIncludeId}
              options={includeOptions}
              setSelectedInput={setIncludeSelect}
              setInputSearchQuery={setIncludeSearchQuery}
              isSearchloading={isIncludeLoading}
              getOptionLabel={getOptionLabel}
            />
            <Typography variant="h6">Included IDs</Typography>
            <MultipleIDsGrid container>
              <Grid item xs={12}>
                <FormInputTextIcon
                  name="includeIds"
                  type="text"
                  control={control}
                  label=""
                  register={register('includeIds', {
                    onChange: (e) => handleIncludeIdsChange(e.target.value),
                  })}
                />
              </Grid>
            </MultipleIDsGrid>
          </Grid>

          {renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            loadingButton
          )}
        </GridForm>
        <NotificationPopup />
      </Dialog>
    </>
  );
};

export default FilterPresetsDialog;
